import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";

const STYLES = `
  .dd-field {
    --pd-s50:    #faf7f2;
    --pd-s100:   #f0e9de;
    --pd-s200:   #e2d5c3;
    --pd-s300:   #c9b89e;
    --pd-t400:   #2a9d8f;
    --pd-dk:     #1a1410;
    --pd-mid:    #3d332a;
    --pd-muted:  #7d6e61;
    --pd-light:  #b0a090;
    --pd-border: #e5ddd3;
    --pd-ibg:    #fdfbf8;
    --pd-white:  #ffffff;
    --pd-glow:   rgba(42, 157, 143, 0.18);
    --pd-err:    #ef4444;
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
    position: relative;
  }
  .dd-label {
    font-size: 9.5px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--pd-muted);
    margin-bottom: 4px;
    display: block;
    font-family: 'Montserrat', sans-serif;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .dd-trigger {
    width: 100%;
    height: 38px;
    padding: 0 32px 0 11px;
    border: 1.5px solid var(--pd-border);
    border-radius: 8px;
    background: var(--pd-ibg);
    font-size: 13px;
    font-family: inherit;
    color: var(--pd-dk);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    text-align: left;
    transition: border-color 0.18s, box-shadow 0.18s;
    position: relative;
    outline: none;
  }
  .dd-trigger:hover:not(.dd-trigger--disabled) { border-color: var(--pd-s300); }
  .dd-trigger--open     { border-color: var(--pd-t400); box-shadow: 0 0 0 3px var(--pd-glow); }
  .dd-trigger--disabled { background: var(--pd-s100); color: var(--pd-light); cursor: not-allowed; }
  .dd-trigger-text        { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .dd-trigger-placeholder { color: var(--pd-light); }
  .dd-chevron {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: var(--pd-muted);
    display: flex;
    align-items: center;
    transition: transform 0.2s ease;
    pointer-events: none;
  }
  .dd-chevron--up { transform: translateY(-50%) rotate(180deg); }
  .dd-panel {
    position: fixed;
    z-index: 99999;
    background: #ffffff;
    border: 1.5px solid #e5ddd3;
    border-radius: 10px;
    box-shadow: 0 8px 32px rgba(26,20,16,0.14), 0 2px 8px rgba(26,20,16,0.07);
    max-height: 240px;
    overflow-y: auto;
    animation: dd-open 0.15s cubic-bezier(0.22,0.9,0.34,1) both;
    scrollbar-width: thin;
    scrollbar-color: #e2d5c3 transparent;
  }
  .dd-panel::-webkit-scrollbar       { width: 4px; }
  .dd-panel::-webkit-scrollbar-track { background: transparent; }
  .dd-panel::-webkit-scrollbar-thumb { background: #e2d5c3; border-radius: 4px; }
  @keyframes dd-open {
    from { opacity: 0; transform: translateY(-6px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .dd-search-wrap {
    display: flex;
    align-items: center;
    gap: 7px;
    padding: 8px 10px;
    border-bottom: 1px solid #f0e9de;
    position: sticky;
    top: 0;
    background: #ffffff;
    z-index: 1;
  }
  .dd-search-icon { color: #b0a090; flex-shrink: 0; }
  .dd-search {
    flex: 1;
    border: none;
    outline: none;
    font-size: 12.5px;
    font-family: inherit;
    color: #1a1410;
    background: transparent;
  }
  .dd-search::placeholder { color: #b0a090; }
  .dd-option {
    padding: 9px 11px;
    font-size: 13px;
    color: #3d332a;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    transition: background 0.12s, color 0.12s;
    font-family: inherit;
  }
  .dd-option:hover              { background: #2a9d8f; color: #ffffff; }
  .dd-option--selected          { color: #2a9d8f; font-weight: 600; background: rgba(42,157,143,0.06); }
  .dd-option--selected:hover    { background: #2a9d8f; color: #ffffff; }
  .dd-option--placeholder       { color: #b0a090; font-size: 12.5px; }
  .dd-option--placeholder:hover { background: #2a9d8f; color: #ffffff; }
  .dd-check                     { color: inherit; flex-shrink: 0; }
  .dd-empty { padding: 12px 11px; font-size: 12px; color: #b0a090; text-align: center; }
  .dd-field--error .dd-trigger       { border-color: #ef4444; }
  .dd-field--error .dd-trigger--open { box-shadow: 0 0 0 3px rgba(239,68,68,0.15); }
  .dd-error { font-size: 11px; color: #ef4444; margin-top: 2px; }
  @media (max-width: 479px) {
    .dd-trigger { height: 44px; font-size: 16px; }
    .dd-option  { font-size: 14px; padding: 11px 12px; }
    .dd-label   { font-size: 9px; }
  }
  @media (hover: none) and (pointer: coarse) {
    .dd-trigger { min-height: 44px; }
    .dd-option  { padding: 12px 11px; }
  }
`;

if (typeof document !== "undefined" && !document.getElementById("dd-styles")) {
  const tag = document.createElement("style");
  tag.id = "dd-styles";
  tag.textContent = STYLES;
  document.head.appendChild(tag);
}

/*
  Props:
    name        {string}
    label       {string}
    options     {string[] | { value, label }[]}
    value       {string}
    onChange    {(value: string) => void}
    placeholder {string}
    disabled    {boolean}
    error       {string}
    onBlur      {fn}
    showSelectOption {boolean}  — show the placeholder/reset option (default: true)
                             pass showSelectOption={false} to hide it
*/
export default function SelectDropdown({
  name,
  label,
  options = [],
  value,
  onChange,
  placeholder = "Select",
  disabled = false,
  error,
  onBlur,
  showSelectOption = true,        // ← NEW PROP
}) {
  const [open, setOpen]             = useState(false);
  const [search, setSearch]         = useState("");
  const [panelStyle, setPanelStyle] = useState({});
  const containerRef                = useRef(null);
  const searchRef                   = useRef(null);

  const normalized = options.map((o) =>
    typeof o === "string" ? { value: o, label: o } : o
  );

  const filtered = normalized.filter((o) =>
    o.label.toLowerCase().includes(search.toLowerCase())
  );

  const selected = normalized.find((o) => o.value === value || o.label === value);

  const updatePanelPosition = () => {
    if (!containerRef.current) return;
    const rect       = containerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openUpward = spaceBelow < 260 && spaceAbove > spaceBelow;
    setPanelStyle({
      position: "fixed",
      left:     rect.left,
      width:    rect.width,
      ...(openUpward
        ? { bottom: window.innerHeight - rect.top + 4, top: "auto" }
        : { top: rect.bottom + 4, bottom: "auto" }),
    });
  };

  const handleOpen = () => {
    if (disabled) return;
    if (!open) updatePanelPosition();
    setOpen((p) => !p);
  };

  useEffect(() => {
    if (!open) return;
    const handler = () => updatePanelPosition();
    window.addEventListener("scroll", handler, true);
    window.addEventListener("resize", handler);
    return () => {
      window.removeEventListener("scroll", handler, true);
      window.removeEventListener("resize", handler);
    };
  }, [open]);

  useEffect(() => {
    const handler = (e) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target) &&
        !e.target.closest(".dd-panel")
      ) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (open && searchRef.current) searchRef.current.focus();
  }, [open]);

  const handleSelect = (opt) => {
    onChange?.(opt.value);
    setOpen(false);
    setSearch("");
    onBlur?.();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape")         { setOpen(false); setSearch(""); }
    if (e.key === "Enter" && !open) handleOpen();
  };

  const panel = open && createPortal(
    <div className="dd-panel" role="listbox" style={panelStyle}>

      {normalized.length > 5 && (
        <div className="dd-search-wrap">
          <svg className="dd-search-icon" width="13" height="13" viewBox="0 0 16 16" fill="none">
            <circle cx="6.5" cy="6.5" r="5" stroke="currentColor" strokeWidth="1.5" />
            <path d="M10.5 10.5l3.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          <input
            ref={searchRef}
            className="dd-search"
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Placeholder/reset option — hidden when showSelectOption={false} */}
      {showSelectOption && (
        <div
          className={`dd-option dd-option--placeholder${!value ? " dd-option--selected" : ""}`}
          onClick={() => handleSelect({ value: "", label: "" })}
          role="option"
        >
          {placeholder}
        </div>
      )}

      {filtered.length > 0 ? (
        filtered.map((opt) => (
          <div
            key={opt.value}
            className={`dd-option${opt.value === value || opt.label === value ? " dd-option--selected" : ""}`}
            onClick={() => handleSelect(opt)}
            role="option"
            aria-selected={opt.value === value}
          >
            {opt.label}
            {(opt.value === value || opt.label === value) && (
              <svg className="dd-check" width="13" height="10" viewBox="0 0 13 10" fill="none">
                <path d="M1 5l4 4L12 1" stroke="currentColor" strokeWidth="1.8"
                  strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </div>
        ))
      ) : (
        <div className="dd-empty">No results found</div>
      )}
    </div>,
    document.body
  );

  return (
    <div
      className={`dd-field${error ? " dd-field--error" : ""}`}
      ref={containerRef}
    >
      {label && <span className="dd-label">{label}</span>}

      <button
        type="button"
        name={name}
        className={[
          "dd-trigger",
          open     ? "dd-trigger--open"     : "",
          disabled ? "dd-trigger--disabled" : "",
        ].filter(Boolean).join(" ")}
        onClick={handleOpen}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={`dd-trigger-text${!selected ? " dd-trigger-placeholder" : ""}`}>
          {selected ? selected.label : placeholder}
        </span>
        <span className={`dd-chevron${open ? " dd-chevron--up" : ""}`}>
          <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
            <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.6"
              strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </button>

      {panel}

      {error && <span className="dd-error">{error}</span>}
    </div>
  );
}